import { useEffect, useState } from 'react';
import { useCart, useCustomer, useSettings, useCartIncentives, useCartUpsell } from 'hooks';
import { useGlobalModules } from 'contexts';
import mergeDisplayLines from 'lib/cart/mergeDisplayLines';

export default function useCartDrawerData(locale) {
  const globalModules = useGlobalModules();
  const { colors, settings, translations, links, media } = globalModules || {};
  const { cart, isEmpty } = useCart();
  const { customer } = useCustomer();
  const { trackedItemsSelectList } = useSettings();
  const [contentfulData, setContentfulData] = useState({});
  const [contentfulLoaded, setContentfulLoaded] = useState(false);

  const { incentives, progressModel, choiceGroups, selectGift } = useCartIncentives(locale);
  const { items: upsellItems } = useCartUpsell(locale);
  const displayLines = mergeDisplayLines(cart?.lines?.edges);
  const giftUnavailable = incentives.some(
    i => i.type === 'freeItem' && i.threshold?.satisfied && i.giftUnavailable
  );

  const subTotalPrice = cart?.cost?.subtotalAmount?.amount;
  const totalPrice = cart?.cost?.totalAmount?.amount;
  const freeShippingPrice = parseFloat(settings?.shipping?.freeShippingPrice || 0);
  const hasShippingBar = settings?.locale?.hasShippingBar !== false;
  const hasSalonSelector = settings?.locale?.hasSalonSelector;

  // Contentful data (slug, size, productSpecs) per line item — unchanged
  useEffect(() => {
    const fetchProducts = async () => {
      const productItems = cart.lines?.edges.map(({ node }) => ({
        slug: node.merchandise?.product?.handle,
        sku: node.merchandise?.sku,
        productId: node.merchandise?.product?.id,
      }));
      try {
        const res = await fetch('/api/contentful/get-products', {
          method: 'POST',
          headers: { 'Content-type': 'application/json' },
          body: JSON.stringify({ productItems, locale }),
        });
        if (res.ok) {
          setContentfulData(await res.json());
        }
      } finally {
        // Settled (ok or not) — view_cart must still fire without category enrichment.
        setContentfulLoaded(true);
      }
    };
    if (cart.lines?.edges?.length) {
      fetchProducts();
    }
  }, [cart.lines?.edges?.length]);

  return {
    locale,
    cart,
    displayLines,
    giftUnavailable,
    isEmpty,
    customer,
    trackedItemsSelectList,
    contentfulData,
    contentfulLoaded,
    subTotalPrice,
    totalPrice,
    freeShippingPrice,
    hasShippingBar,
    hasSalonSelector,
    incentives,
    progressModel,
    choiceGroups,
    selectGift,
    upsellItems,
    colors,
    settings,
    translations,
    links,
    media,
  };
}
